import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { AppDispatch, RootState } from '../store/store';
import { fetchLicenses } from '../store/slices/licenseSlice';
import { helper } from '../utils/helper';
import { isTrialSubscription } from '../utils/subscriptionHelpers';
import type { SubscriptionView } from '../api/generated/models/SubscriptionView';
import EmptyState from './EmptyState';
import Pagination from './Pagination';
import { useTranslation } from 'react-i18next';

const LicensesTab: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { items, total, page, pageSize, loading, error, isLoaded } =
    useSelector((state: RootState) => state.licenses);

  const handlePageChange = (direction: 'prev' | 'next') => {
    const newPage = direction === 'next' ? page + 1 : page - 1;
    dispatch(fetchLicenses({ page: newPage, pageSize }));
  };

  const totalPages = Math.ceil(total / pageSize);
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  const getSubscriptionStatus = (subscription: SubscriptionView) => {
    // Pre-created future subscription (right side of a scheduled downgrade).
    // It is not active yet; its start date equals the current period end of
    // the active subscription of the same plugin.
    if (subscription.isScheduled) {
      const startRaw = items.find(
        (s) =>
          s.pluginName === subscription.pluginName && s.isActive && s.expired,
      )?.expired;
      return (
        <span className="status-warning">
          {startRaw
            ? t('subscription_status_scheduled_with_date', {
                date: helper.formatDate(startRaw),
              })
            : t('subscription_status_scheduled')}
        </span>
      );
    }

    // Blocked by a failed (unpaid) Stripe invoice.
    if (subscription.hasUnpaidInvoice) {
      return (
        <span className="status-error">{t('license_status_unpaid')}</span>
      );
    }

    if (!subscription.expired) {
      return subscription.isActive ? (
        <span className="status-success">{t('active')}</span>
      ) : (
        <span className="status-error">{t('inactive')}</span>
      );
    }

    const isActive = helper.isSubscriptionActive(subscription);
    const today = new Date();
    const expireDate = new Date(subscription.expired);
    const diffTime = Math.abs(expireDate.getTime() - today.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (!isActive) {
      // Deactivated before the period end: the subscription was cancelled,
      // not naturally expired, so "expired <future date>" would be wrong.
      if (expireDate > today) {
        return (
          <span className="status-error">{t('license_status_cancelled')}</span>
        );
      }
      return (
        <span className="status-error">
          {t('expired')} {helper.formatDate(subscription.expired)} (
          {t('days_ago', { count: diffDays })})
        </span>
      );
    }

    return (
      <span className="status-success">
        {t('expires')} {helper.formatDate(subscription.expired)} (
        {t('in_days', { count: diffDays })})
      </span>
    );
  };

  // Active trial (auto-granted on registration): banner with the expiry date
  // and a link to the regular buy flow. Highlighted as a warning when <= 3
  // days remain. Trial subscriptions always carry an `expired` date.
  const activeTrial = items.find(
    (s) => isTrialSubscription(s) && helper.isSubscriptionActive(s),
  );
  const trialDaysLeft = activeTrial?.expired
    ? Math.ceil(
        (new Date(activeTrial.expired).getTime() - Date.now()) /
          (1000 * 60 * 60 * 24),
      )
    : null;

  if (!isLoaded) {
    return null;
  }

  return (
    <div>
      <div className="licenses-content">
        <h1>{t('licenses')}</h1>
        {activeTrial && activeTrial.expired && (
          <div
            className={`trial-banner${trialDaysLeft !== null && trialDaysLeft <= 3 ? ' trial-banner--warning' : ''}`}
            role="status"
          >
            <span className="trial-banner-text">
              {t('subscription_trial_banner', {
                date: helper.formatDateOnly(activeTrial.expired),
              })}
            </span>
            <Link
              className="trial-banner-button"
              to={`/licenses/buy?plugin=${activeTrial.pluginName || 'mobile'}`}
            >
              {t('subscription_trial_banner_buy')}
            </Link>
          </div>
        )}
        <div className="table-container licenses-table-container">
          {items.length > 0 ? (
            <>
              <table className="table licenses-table">
                <thead>
                  <tr>
                    <th>{t('product_name')}</th>
                    <th>{t('payment_type')}</th>
                    <th>{t('validLicenseStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((subscription) => (
                    <tr key={subscription.id}>
                      <td>{subscription.productName}</td>
                      <td className="licenses-table-type">
                        {subscription.paymentType}
                      </td>
                      <td>{getSubscriptionStatus(subscription)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                startItem={startItem}
                endItem={endItem}
                total={total}
                totalPages={totalPages}
                page={page}
                loading={loading}
                handlePageChange={handlePageChange}
              />
            </>
          ) : (
            <EmptyState
              title={t('no_licenses_yet')}
              description={t('no_licenses_description')}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LicensesTab;
